import * as React from "react";
import LoadingOverlay from "@/components/ui/LoadingOverlay";
import { cn } from "@/utils";

const DEVICE_SIZES = [320, 420, 640, 768, 1024, 1280, 1536, 1920];
const IMAGE_SIZES = [16, 32, 48, 64, 96, 128, 256, 384];

export type ImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

export type ImageLoader = (props: ImageLoaderProps) => string;

export interface ImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "width" | "height"> {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  fill?: boolean;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  placeholder?: "empty" | "blur";
  blurDataURL?: string;
  loader?: ImageLoader;
  unoptimized?: boolean;
  wrapperClassName?: string;
  objectFit?: React.CSSProperties["objectFit"];
  objectPosition?: React.CSSProperties["objectPosition"];
  onLoadingComplete?: (img: HTMLImageElement) => void;
}

const defaultLoader: ImageLoader = ({ src }) => src;

function toNumber(value?: number | string): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function formatDimension(value?: number | string): string | undefined {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
}

function matchClosestSize(target: number): number {
  for (const size of IMAGE_SIZES) {
    if (size >= target) {
      return size;
    }
  }
  return DEVICE_SIZES[DEVICE_SIZES.length - 1];
}

function getCandidateWidths({
  width,
  sizes,
  fill
}: {
  width?: number;
  sizes?: string;
  fill?: boolean;
}): number[] {
  if (fill || sizes) {
    return DEVICE_SIZES;
  }

  if (typeof width === "number" && width > 0) {
    const targets = new Set<number>([width, width * 2]);
    return Array.from(targets)
      .map((target) => matchClosestSize(target))
      .sort((a, b) => a - b);
  }

  return DEVICE_SIZES;
}

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    src,
    alt,
    width,
    height,
    fill = false,
    sizes,
    quality = 75,
    priority = false,
    placeholder = "empty",
    blurDataURL,
    loader = defaultLoader,
    unoptimized = false,
    wrapperClassName,
    className,
    objectFit,
    objectPosition,
    onLoadingComplete,
    style,
    ...imgProps
  },
  ref
) {
  const {
    loading: loadingProp,
    fetchPriority: fetchPriorityProp,
    decoding: decodingProp,
    onLoad: onLoadProp,
    ...restImgProps
  } = imgProps;

  const widthNumber = toNumber(width);
  const heightNumber = toNumber(height);

  React.useEffect(() => {
    if (!fill && (!widthNumber || !heightNumber)) {
      console.warn(
        "[Image] When `fill` is false you should provide both `width` and `height` to prevent layout shifts."
      );
    }
  }, [fill, heightNumber, widthNumber]);

  const candidateWidths = React.useMemo(
    () => getCandidateWidths({ width: widthNumber, sizes, fill }),
    [fill, sizes, widthNumber]
  );

  // Keep blurDataURL around to avoid passing it through to the DOM while the API transitions.
  void blurDataURL;

  const largestWidth =
    candidateWidths[candidateWidths.length - 1] ?? widthNumber ?? DEVICE_SIZES[DEVICE_SIZES.length - 1];

  const computedSrc = React.useMemo(() => {
    if (unoptimized) {
      return src;
    }

    return loader({ src, width: largestWidth, quality });
  }, [largestWidth, loader, quality, src, unoptimized]);

  const computedSrcSet = React.useMemo(() => {
    if (unoptimized) {
      return undefined;
    }

    return candidateWidths
      .map((currentWidth) => `${loader({ src, width: currentWidth, quality })} ${currentWidth}w`)
      .join(", ");
  }, [candidateWidths, loader, quality, src, unoptimized]);

  const sizesValue =
    sizes ??
    (fill ? "100vw"
    : widthNumber ? `${widthNumber}px`
    : undefined);

  const [isLoaded, setIsLoaded] = React.useState(false);
  const internalRef = React.useRef<HTMLImageElement | null>(null);

  const setRefs = React.useCallback(
    (node: HTMLImageElement | null) => {
      internalRef.current = node;
      if (!ref) {
        return;
      }
      if (typeof ref === "function") {
        ref(node);
      } else {
        ref.current = node;
      }
    },
    [ref]
  );

  React.useEffect(() => {
    const image = internalRef.current;
    if (!image) {
      return;
    }

    if (image.complete && image.naturalWidth > 0) {
      setIsLoaded(true);
      onLoadingComplete?.(image);
    } else {
      setIsLoaded(false);
    }
  }, [onLoadingComplete, src]);

  const handleLoad = React.useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setIsLoaded(true);
      onLoadProp?.(event);
      onLoadingComplete?.(event.currentTarget);
    },
    [onLoadProp, onLoadingComplete]
  );

  const loading = priority ? "eager" : (loadingProp ?? "lazy");
  const fetchPriority = priority ? "high" : fetchPriorityProp;
  const decoding = decodingProp ?? "async";

  const resolvedObjectFit = objectFit ?? (fill ? "cover" : undefined);

  const wrapperStyle = React.useMemo<React.CSSProperties>(
    () => ({
      position: "relative",
      display: fill ? "block" : "inline-block",
      overflow: "hidden",
      width: fill ? "100%" : (formatDimension(width) ?? undefined),
      height: fill ? "100%" : (formatDimension(height) ?? undefined)
    }),
    [fill, height, width]
  );

  const imageStyle = React.useMemo<React.CSSProperties>(
    () => ({
      width: fill ? "100%" : (formatDimension(width) ?? undefined),
      height: fill ? "100%" : (formatDimension(height) ?? undefined),
      position: fill ? "absolute" : undefined,
      inset: fill ? 0 : undefined,
      objectFit: resolvedObjectFit,
      objectPosition,
      ...style
    }),
    [fill, height, objectPosition, resolvedObjectFit, style, width]
  );

  const shouldShowLoadingOverlay = placeholder === "blur";

  return (
    <span
      data-slot="image"
      className={cn("relative overflow-hidden", fill ? "block" : "inline-block", wrapperClassName)}
      style={wrapperStyle}
    >
      {shouldShowLoadingOverlay && (
        <LoadingOverlay
          aria-hidden
          fullscreen={false}
          spinnerClassName="size-6"
          className={cn(
            // "bg-white/75!",
            isLoaded ? "opacity-0 pointer-events-none" : "opacity-100"
          )}
        />
      )}
      <img
        ref={setRefs}
        className={cn(
          "block transition-opacity duration-300",
          fill && "absolute inset-0 size-full",
          shouldShowLoadingOverlay && !isLoaded ? "opacity-0" : "opacity-100",
          className
        )}
        src={computedSrc}
        srcSet={computedSrcSet}
        sizes={sizesValue}
        width={fill ? undefined : widthNumber}
        height={fill ? undefined : heightNumber}
        alt={alt}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding={decoding}
        onLoad={handleLoad}
        style={imageStyle}
        {...restImgProps}
      />
    </span>
  );
});

Image.displayName = "Image";
